import React, { useState, useEffect } from 'react';
import { XIcon, PlusIcon } from 'lucide-react';
import { tenantApi } from '../lib/tenant-api';
import { Asset } from '../pages/AssetsManagement';

interface AssetTagEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  asset: Asset | null;
  onTagsUpdated: (asset: Asset) => void;
}

const AssetTagEditModal: React.FC<AssetTagEditModalProps> = ({
  isOpen,
  onClose,
  asset,
  onTagsUpdated,
}) => {
  const [tags, setTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTagInput, setShowTagInput] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [assetName, setAssetName] = useState('');

  useEffect(() => {
    if (isOpen && asset) {
      setTags(asset.tags || []);
      setAssetName(asset.name);
      setTagInput('');
      setShowTagInput(false);
      setError(null);

      const loadAvailableTags = async () => {
        try {
          const response = await tenantApi.getAssetTags();
          setAvailableTags(response.tags);
        } catch (err) {
          console.error('Failed to load available tags:', err);
        }
      };
      loadAvailableTags();
    }
  }, [isOpen, asset]);

  const addTag = (value?: string) => {
    const tag = (value || tagInput).trim().toLowerCase();
    if (tag && !tags.includes(tag)) {
      setTags((prev) => [...prev, tag]);
    }
    setTagInput('');
  };

  const removeTag = (tagToRemove: string) => {
    setTags((prev) => prev.filter((t) => t !== tagToRemove));
  };

  const handleSave = async () => {
    if (!asset) return;
    setIsSaving(true);
    setError(null);

    try {
      const updatedAsset = await tenantApi.updateAssetTags(asset.id, tags);
      onTagsUpdated({
        ...asset,
        tags: updatedAsset.tags || [],
      });
      onClose();
    } catch (err) {
      console.error('Failed to update asset tags:', err);
      setError('Failed to update tags. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setTags([]);
      setTagInput('');
      setShowTagInput(false);
      setError(null);
      onClose();
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + sizes[i];
  };

  if (!isOpen || !asset) return null;

  const suggestedTags = availableTags.filter((t) => !tags.includes(t)).slice(0, 4);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose} />

      {/* Panel */}
      <div className="relative bg-white w-full max-w-2xl flex flex-col rounded shadow-xl mx-4">
        {/* Header */}
        <div className="px-8 pt-8 pb-4 flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Edit Tag</h2>
            <p className="mt-1 text-sm text-gray-500">Update the tag linked to this asset</p>
          </div>
          <button onClick={handleClose} className="p-1 text-gray-400 hover:text-gray-600">
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-8 pb-4 space-y-6">
          {/* Error */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Asset card */}
          <div className="border border-gray-200 rounded p-4">
            <div className="flex gap-4">
              {/* Thumbnail */}
              <div className="flex-shrink-0 w-28 h-28 bg-gray-100 rounded overflow-hidden">
                {asset.thumbnailUrl ? (
                  <img
                    src={asset.thumbnailUrl}
                    alt={asset.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <span className="text-xs font-medium uppercase">{asset.type}</span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0 space-y-2">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Asset Name</label>
                  <input
                    type="text"
                    value={assetName}
                    onChange={(e) => setAssetName(e.target.value)}
                    className="block w-full max-w-xs px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-gray-50"
                    disabled
                  />
                  <p className="text-xs text-gray-400">{formatFileSize(asset.size)}</p>
                </div>

                {/* Tags */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">Tags</label>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200"
                      >
                        {tag}
                        <button
                          onClick={() => removeTag(tag)}
                          className="ml-1 text-indigo-500 hover:text-indigo-700"
                          disabled={isSaving}
                        >
                          <XIcon className="h-2.5 w-2.5" />
                        </button>
                      </span>
                    ))}
                    {showTagInput ? (
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            addTag();
                          }
                        }}
                        onBlur={() => {
                          if (tagInput.trim()) addTag();
                          setShowTagInput(false);
                        }}
                        placeholder="Enter tag name"
                        className="px-2 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 w-28"
                        autoFocus
                        disabled={isSaving}
                      />
                    ) : (
                      <button
                        onClick={() => {
                          setShowTagInput(true);
                          setTagInput('');
                        }}
                        className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                        disabled={isSaving}
                      >
                        <PlusIcon className="h-2.5 w-2.5 mr-0.5" />
                        Add Tag
                      </button>
                    )}
                  </div>

                  {/* Suggestions */}
                  {suggestedTags.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs text-gray-400">Suggestions:</span>
                      {suggestedTags.map((tag) => (
                        <button
                          key={tag}
                          onClick={() => addTag(tag)}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                          disabled={isSaving}
                        >
                          <PlusIcon className="h-2.5 w-2.5 mr-0.5" />
                          {tag}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <span className="flex items-center">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Saving...
              </span>
            ) : (
              'Edit Tag'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetTagEditModal;
